import React, { useMemo, useState } from 'react';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import EmptyState from './EmptyState';
import LoadingSpinner from './LoadingSpinner';
import { cn } from '../utils/cn';

/**
 * Columns: [{ key, header, render?: (row) => ReactNode, accessor?: (row) => any, width?, className? }]
 * onSearch is local-only (filters by stringifying every accessor).
 */
function DataTable({
  columns = [],
  rows = [],
  loading = false,
  search = true,
  searchPlaceholder = 'Search…',
  pageSize = 10,
  emptyTitle = 'No results',
  emptyMessage = 'Nothing matches your filters yet.',
  toolbarLeft,
  toolbarRight
}) {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.toLowerCase();
    return rows.filter((row) =>
      columns.some((c) => {
        const v = c.accessor ? c.accessor(row) : row[c.key];
        return v != null && String(v).toLowerCase().includes(needle);
      })
    );
  }, [q, rows, columns]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageRows = filtered.slice(pageStart, pageStart + pageSize);

  return (
    <div className="table-card">
      {(search || toolbarLeft || toolbarRight) && (
        <div className="table-toolbar">
          {search && (
            <div className="topbar-search grow" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <Search size={16} color="var(--slate-400)" />
              <input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder={searchPlaceholder}
              />
            </div>
          )}
          {toolbarLeft}
          <div className="spacer" />
          {toolbarRight}
        </div>
      )}

      {loading ? (
        <LoadingSpinner label="Loading…" />
      ) : pageRows.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyMessage} />
      ) : (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} style={{ width: c.width }}>{c.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, i) => (
                  <tr key={row._id || row.id || i}>
                    {columns.map((c) => (
                      <td key={c.key} className={cn(c.className)}>
                        {c.render ? c.render(row) : (c.accessor ? c.accessor(row) : row[c.key]) ?? '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-pagination">
            <div>
              Showing {pageStart + 1}-{Math.min(pageStart + pageRows.length, filtered.length)} of {filtered.length}
            </div>
            <div className="pager">
              <button
                className="btn btn-ghost btn-sm"
                disabled={safePage === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <span className="muted" style={{ padding: '0 var(--s-2)' }}>
                Page {safePage} of {totalPages}
              </span>
              <button
                className="btn btn-ghost btn-sm"
                disabled={safePage === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DataTable;
